import {
  DiscountDistributionMethod,
  ExchangesSelection,
  ExchangeType,
  ShopSiteNavigationMode,
  type InstantExchangeHoldKind,
} from "@redotech/redo-return-core-common/exchange-settings";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  BundleRestockingType,
  BundleReturnMode,
} from "./bundle-data-constants";
import { sameSettingsValue } from "./grouped-settings-changes";
import type { ReturnTogetherConfig } from "./return-together";
import { ReturnTogetherSelector } from "./return-together-constants";
import {
  diffSettingsGroups,
  mergeSettingsGroups,
  type SettingsGroups,
} from "./tab-settings/settings-group-diff";

/**
 * How exchange orders inherit the original order's discounts. One merchant
 * choice spanning five stored fields, so the portal presents it as one row.
 */
export enum DiscountLogic {
  NONE = "none",
  REAPPLY_CODE = "reapply_code",
  REAPPLY_AMOUNT = "reapply_amount",
  SHOP_NOW_BONUS = "shop_now_bonus",
}

const INSTANT_HOLD_KINDS = ["none", "one_dollar", "full_cost"] as const;

const TagExclusionRuleSchema = z.object({
  sourceTag: z.string(),
  excludedTags: z.array(z.string()),
});

export const ReturnsPortalSettingsSchema = z.object({
  display: z.object({ languageSupportEnabled: z.boolean() }),
  exchangeAgent: z.object({
    enabled: z.boolean(),
    displayExchangeValueAsDiscount: z.boolean(),
    variantRecommendationsEnabled: z.boolean(),
    productRecommendationsEnabled: z.boolean(),
    productRecommendationsLimit: z.number().int().min(1).max(20),
    tagExclusionRules: z.array(TagExclusionRuleSchema),
  }),
  exchangeUi: z.object({
    selection: createEnum(ExchangesSelection),
    checkoutSelector: z.string(),
    shopSiteURL: z.string(),
    shopSiteNewTab: z.boolean(),
    showFullPriceOnly: z.boolean(),
    bannerColor: z.string(),
    bannerFontColor: z.string(),
    bannerFont: z.string(),
    bannerText: z.string(),
    bannerShowDoneButton: z.boolean(),
    bannerPositionBottom: z.boolean(),
    bannerInsertBeforeBody: z.boolean(),
    bannerCustomCss: z.string(),
    /** Read by the shop-now catalog to tint variant swatches. */
    variantPickerColorHexMetafield: z.string(),
  }),
  pricing: z.object({
    discountLogic: createEnum(DiscountLogic),
    applyOriginalOrderDiscounts: z.array(createEnum(ExchangeType)),
    applyAutomaticDiscounts: z.array(createEnum(ExchangeType)),
    applyShopOnSiteCartDiscounts: z.boolean(),
    recreateExpiredDiscounts: z.boolean(),
    recreateAutomaticDiscountsAsCodes: z.boolean(),
    reapplyCompareAtDiscount: z.boolean(),
    allowCustomerDiscountCodes: z.boolean(),
    differentPricedVariantExchanges: z.boolean(),
    excludeDifferentPriceVariants: z.boolean(),
    variantExchangeProductTags: z.array(z.string()),
  }),
  customerOptions: z.object({
    instant: z.boolean(),
    instantHoldAmount: z.enum(INSTANT_HOLD_KINDS),
    instantExchangeOnly: z.boolean(),
    instantExpirationDays: z.number().int().min(0),
    excludeProductTags: z.array(z.string()),
    excludeProductSkus: z.array(z.string()),
    excludeProductMetafields: z.array(z.string()),
    excludeVariantMetafields: z.array(z.string()),
    inventoryMinimum: z.number().int().min(0),
    hideOutOfStockVariants: z.boolean(),
    hideUnpublishedVariants: z.boolean(),
  }),
  returnEditing: z.object({
    allowCustomerCancelReturn: z.boolean(),
    allowCustomerEditExchangeAddress: z.boolean(),
  }),
  discountDistribution: z.object({
    method: createEnum(DiscountDistributionMethod).nullable(),
    redistributeBogoDiscounts: z.boolean(),
  }),
  bundles: z.object({
    returnMode: createEnum(BundleReturnMode),
    restockingType: createEnum(BundleRestockingType),
    useShopifyNativeBundles: z.boolean(),
    returnTogetherProperties: z.array(z.string()),
  }),
  shippingOptions: z.object({
    deductLabelFromRefund: z.boolean(),
    deductLabelFromCredit: z.boolean(),
    deductLabelFromExchange: z.boolean(),
  }),
});

export type ReturnsPortalSettings = z.infer<typeof ReturnsPortalSettingsSchema>;

const settingsShape = ReturnsPortalSettingsSchema.shape;

/** Only the fields a merchant changed; everything omitted is left as stored. */
export const ReturnsPortalSettingsChangesSchema = z.object({
  display: settingsShape.display.partial().optional(),
  exchangeAgent: settingsShape.exchangeAgent.partial().optional(),
  exchangeUi: settingsShape.exchangeUi.partial().optional(),
  pricing: settingsShape.pricing.partial().optional(),
  customerOptions: settingsShape.customerOptions.partial().optional(),
  returnEditing: settingsShape.returnEditing.partial().optional(),
  discountDistribution: settingsShape.discountDistribution.partial().optional(),
  bundles: settingsShape.bundles.partial().optional(),
  shippingOptions: settingsShape.shippingOptions.partial().optional(),
});

export type ReturnsPortalSettingsChanges = z.infer<
  typeof ReturnsPortalSettingsChangesSchema
>;

export interface ReturnsPortalTeamPatch {
  /** Dotted paths relative to `team.settings`. */
  set: Record<string, unknown>;
  unset: string[];
}

const SETTINGS_GROUP_KEYS = Object.keys(settingsShape);

/** The slice of team settings the Portal tab reads. Structurally matches `Team["settings"]`. */
export interface ReturnsPortalSettingsSource {
  settings: {
    checkoutSelector?: string | null;
    exchangeBannerColor?: string | null;
    exchangeBannerText?: string | null;
    exchangeBannerFont?: string | null;
    exchangeBannerFontColor?: string | null;
    exchangeBannerShowDoneButton?: boolean | null;
    exchangeBannerCustomCss?: string | null;
    discountDistributionMethod?: DiscountDistributionMethod | null;
    deductLabelFromRefund?: boolean | null;
    deductLabelFromCredit?: boolean | null;
    deductLabelFromExchange?: boolean | null;
    localization?: { enabled?: boolean | null } | null;
    shopifyExtension?: {
      exchangeBannerPositionBottom?: boolean | null;
      exchangeBannerInsertBeforeBody?: boolean | null;
    } | null;
    inventory: { minimum_stock: number };
    exchangeAgent: {
      enabled?: boolean | null;
      displayExchangeValueAsDiscount?: boolean | null;
      variantRecommendations: { enabled?: boolean | null };
      productRecommendations: {
        enabled?: boolean | null;
        limit?: number | null;
        tagExclusionRules?:
          | { sourceTag: string; excludedTags?: string[] | null }[]
          | null;
      };
    };
    exchanges: {
      selection: ExchangesSelection | null;
      shopSiteURL?: string | null;
      shopSite?: { navigationMode?: ShopSiteNavigationMode | null } | null;
      exchangeOptionsPreviewShowOriginalPrice?: boolean | null;
      variantPickerColorHexMetafield?: string | null;
      allowInstant: boolean;
      instantHoldAmount: InstantExchangeHoldKind;
      instantExchangeOnly?: boolean | null;
      instantExpirationDays: number;
      excludeProductTags?: string[] | null;
      excludeProductSkus?: string[] | null;
      excludeProductMetafields?: string[] | null;
      excludeVariantMetafields?: string[] | null;
      hideOutOfStockVariants?: boolean | null;
      hideUnpublishedVariants?: boolean | null;
      differentPricedVariantExchanges: boolean;
      excludeDifferentPriceVariants?: boolean | null;
      variantExchangeProductTags?: string[] | null;
      allowCustomerDiscountCodes?: boolean | null;
      reapplyDiscountAmount?: boolean | null;
      usePreDiscountPrice?: boolean | null;
      reapplyCompareAtDiscount?: boolean | null;
      recreateExpiredDiscounts: boolean;
      recreateAutomaticDiscountsAsCodes: boolean;
      shopSiteUseCartDiscounts?: boolean | null;
      applyOriginalOrderDiscounts?: ExchangeType[] | null;
      applyAutomaticDiscounts?: ExchangeType[] | null;
    };
    returns?: {
      redistributeBogoDiscounts?: boolean | null;
      bundleReturnMode?: BundleReturnMode | null;
      bundleRestockingType?: BundleRestockingType | null;
      useShopifyNativeBundles?: boolean | null;
      returnTogether?: { selector?: string; value?: string }[] | null;
      returnEditing?: {
        allowCustomerCancelReturn?: boolean | null;
        allowCustomerEditExchangeAddress?: boolean | null;
      } | null;
    } | null;
  };
}

function settingsGroups(settings: ReturnsPortalSettings): SettingsGroups {
  return {
    display: settings.display,
    exchangeAgent: settings.exchangeAgent,
    exchangeUi: settings.exchangeUi,
    pricing: settings.pricing,
    customerOptions: settings.customerOptions,
    returnEditing: settings.returnEditing,
    discountDistribution: settings.discountDistribution,
    bundles: settings.bundles,
    shippingOptions: settings.shippingOptions,
  };
}

/**
 * One stored field per branch, checked in the order the customer portal reads
 * them: an amount reapply wins over a Shop Now bonus, which wins over codes.
 */
function discountLogicFromSettings(exchanges: {
  reapplyDiscountAmount?: boolean | null;
  usePreDiscountPrice?: boolean | null;
  applyOriginalOrderDiscounts?: ExchangeType[] | null;
  applyAutomaticDiscounts?: ExchangeType[] | null;
}): DiscountLogic {
  if (exchanges.reapplyDiscountAmount) {
    return DiscountLogic.REAPPLY_AMOUNT;
  }
  if (exchanges.usePreDiscountPrice) {
    return DiscountLogic.SHOP_NOW_BONUS;
  }
  const codes =
    (exchanges.applyOriginalOrderDiscounts?.length ?? 0) > 0 ||
    (exchanges.applyAutomaticDiscounts?.length ?? 0) > 0;
  return codes ? DiscountLogic.REAPPLY_CODE : DiscountLogic.NONE;
}

export function returnsPortalSettingsFromTeam({
  settings,
}: ReturnsPortalSettingsSource): ReturnsPortalSettings {
  const { exchanges, exchangeAgent, returns } = settings;
  return {
    display: { languageSupportEnabled: !!settings.localization?.enabled },
    exchangeAgent: {
      enabled: exchangeAgent.enabled ?? true,
      displayExchangeValueAsDiscount:
        exchangeAgent.displayExchangeValueAsDiscount ?? true,
      variantRecommendationsEnabled:
        exchangeAgent.variantRecommendations.enabled ?? true,
      productRecommendationsEnabled:
        exchangeAgent.productRecommendations.enabled ?? true,
      productRecommendationsLimit:
        exchangeAgent.productRecommendations.limit ?? 12,
      tagExclusionRules: (
        exchangeAgent.productRecommendations.tagExclusionRules ?? []
      ).map((rule) => ({
        sourceTag: rule.sourceTag,
        excludedTags: rule.excludedTags ?? [],
      })),
    },
    exchangeUi: {
      selection: exchanges.selection ?? ExchangesSelection.RETURN_APP,
      checkoutSelector: settings.checkoutSelector ?? "",
      shopSiteURL: exchanges.shopSiteURL ?? "",
      shopSiteNewTab:
        exchanges.shopSite?.navigationMode === ShopSiteNavigationMode.NEW_TAB,
      showFullPriceOnly:
        exchanges.exchangeOptionsPreviewShowOriginalPrice ?? false,
      bannerColor: settings.exchangeBannerColor ?? "",
      bannerFontColor: settings.exchangeBannerFontColor ?? "",
      bannerFont: settings.exchangeBannerFont ?? "",
      bannerText: settings.exchangeBannerText ?? "",
      bannerShowDoneButton: settings.exchangeBannerShowDoneButton ?? true,
      bannerPositionBottom:
        settings.shopifyExtension?.exchangeBannerPositionBottom ?? false,
      bannerInsertBeforeBody:
        settings.shopifyExtension?.exchangeBannerInsertBeforeBody ?? false,
      bannerCustomCss: settings.exchangeBannerCustomCss ?? "",
      variantPickerColorHexMetafield:
        exchanges.variantPickerColorHexMetafield ?? "",
    },
    pricing: {
      discountLogic: discountLogicFromSettings(exchanges),
      applyOriginalOrderDiscounts: exchanges.applyOriginalOrderDiscounts ?? [],
      applyAutomaticDiscounts: exchanges.applyAutomaticDiscounts ?? [],
      applyShopOnSiteCartDiscounts: exchanges.shopSiteUseCartDiscounts ?? true,
      recreateExpiredDiscounts: exchanges.recreateExpiredDiscounts,
      recreateAutomaticDiscountsAsCodes:
        exchanges.recreateAutomaticDiscountsAsCodes,
      reapplyCompareAtDiscount: exchanges.reapplyCompareAtDiscount ?? false,
      allowCustomerDiscountCodes: exchanges.allowCustomerDiscountCodes ?? true,
      differentPricedVariantExchanges:
        exchanges.differentPricedVariantExchanges,
      excludeDifferentPriceVariants:
        exchanges.excludeDifferentPriceVariants ?? false,
      variantExchangeProductTags: exchanges.variantExchangeProductTags ?? [],
    },
    customerOptions: {
      instant: exchanges.allowInstant,
      instantHoldAmount: exchanges.instantHoldAmount,
      instantExchangeOnly: exchanges.instantExchangeOnly ?? false,
      instantExpirationDays: exchanges.instantExpirationDays,
      excludeProductTags: exchanges.excludeProductTags ?? [],
      excludeProductSkus: exchanges.excludeProductSkus ?? [],
      excludeProductMetafields: exchanges.excludeProductMetafields ?? [],
      excludeVariantMetafields: exchanges.excludeVariantMetafields ?? [],
      inventoryMinimum: settings.inventory.minimum_stock,
      hideOutOfStockVariants: exchanges.hideOutOfStockVariants ?? false,
      hideUnpublishedVariants: exchanges.hideUnpublishedVariants ?? true,
    },
    returnEditing: {
      allowCustomerCancelReturn:
        returns?.returnEditing?.allowCustomerCancelReturn ?? false,
      allowCustomerEditExchangeAddress:
        returns?.returnEditing?.allowCustomerEditExchangeAddress ?? false,
    },
    discountDistribution: {
      method: settings.discountDistributionMethod ?? null,
      redistributeBogoDiscounts: !!returns?.redistributeBogoDiscounts,
    },
    bundles: {
      returnMode: returns?.bundleReturnMode ?? BundleReturnMode.ALWAYS_SEPARATE,
      restockingType:
        returns?.bundleRestockingType ?? BundleRestockingType.PARENT,
      useShopifyNativeBundles: returns?.useShopifyNativeBundles ?? false,
      returnTogetherProperties: (returns?.returnTogether ?? [])
        .filter(
          (config) =>
            config.selector === ReturnTogetherSelector.LINE_ITEM_PROPERTY,
        )
        .map((config) => config.value ?? ""),
    },
    shippingOptions: {
      deductLabelFromRefund: !!settings.deductLabelFromRefund,
      deductLabelFromCredit: !!settings.deductLabelFromCredit,
      deductLabelFromExchange: !!settings.deductLabelFromExchange,
    },
  };
}

export function returnsPortalSettingsChanges(
  initial: ReturnsPortalSettings,
  next: ReturnsPortalSettings,
): ReturnsPortalSettingsChanges {
  return ReturnsPortalSettingsChangesSchema.parse(
    diffSettingsGroups(
      SETTINGS_GROUP_KEYS,
      settingsGroups(initial),
      settingsGroups(next),
    ),
  );
}

function applyChanges(
  base: ReturnsPortalSettings,
  changes: ReturnsPortalSettingsChanges,
): ReturnsPortalSettings {
  return ReturnsPortalSettingsSchema.parse(
    mergeSettingsGroups(SETTINGS_GROUP_KEYS, settingsGroups(base), changes),
  );
}

/** Drops blanks and duplicates, so a half-typed row never becomes a config. */
function returnTogetherConfigs(properties: string[]): ReturnTogetherConfig[] {
  const seen = new Set<string>();
  const configs: ReturnTogetherConfig[] = [];
  for (const property of properties) {
    const trimmed = property.trim();
    if (!trimmed || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    configs.push({
      selector: ReturnTogetherSelector.LINE_ITEM_PROPERTY,
      value: trimmed,
    });
  }
  return configs;
}

export function returnsPortalSettingsToTeamPatch(
  next: ReturnsPortalSettings,
): ReturnsPortalTeamPatch {
  const {
    display,
    exchangeAgent,
    exchangeUi,
    pricing,
    customerOptions,
    returnEditing,
    discountDistribution,
    bundles,
    shippingOptions,
  } = next;

  // Everything under "reapply discount codes" is stored as its own field, and
  // the portal reads them independently. Clear them together when the merchant
  // picks another method, or a stale sub-toggle keeps applying.
  const reapplyCodes = pricing.discountLogic === DiscountLogic.REAPPLY_CODE;

  const set: Record<string, unknown> = {
    "localization.enabled": display.languageSupportEnabled,

    "exchangeAgent.enabled": exchangeAgent.enabled,
    "exchangeAgent.displayExchangeValueAsDiscount":
      exchangeAgent.displayExchangeValueAsDiscount,
    "exchangeAgent.variantRecommendations.enabled":
      exchangeAgent.variantRecommendationsEnabled,
    "exchangeAgent.productRecommendations.enabled":
      exchangeAgent.productRecommendationsEnabled,
    "exchangeAgent.productRecommendations.limit":
      exchangeAgent.productRecommendationsLimit,
    "exchangeAgent.productRecommendations.tagExclusionRules":
      exchangeAgent.tagExclusionRules,

    "exchanges.selection": exchangeUi.selection,
    "exchanges.shopSiteURL": exchangeUi.shopSiteURL,
    "exchanges.shopSite.navigationMode": exchangeUi.shopSiteNewTab
      ? ShopSiteNavigationMode.NEW_TAB
      : ShopSiteNavigationMode.SAME_TAB,
    "exchanges.exchangeOptionsPreviewShowOriginalPrice":
      exchangeUi.showFullPriceOnly,
    exchangeBannerColor: exchangeUi.bannerColor,
    exchangeBannerFontColor: exchangeUi.bannerFontColor,
    exchangeBannerFont: exchangeUi.bannerFont,
    exchangeBannerText: exchangeUi.bannerText,
    exchangeBannerShowDoneButton: exchangeUi.bannerShowDoneButton,
    exchangeBannerCustomCss: exchangeUi.bannerCustomCss,
    "shopifyExtension.exchangeBannerPositionBottom":
      exchangeUi.bannerPositionBottom,
    "shopifyExtension.exchangeBannerInsertBeforeBody":
      exchangeUi.bannerInsertBeforeBody,

    "exchanges.reapplyDiscountAmount":
      pricing.discountLogic === DiscountLogic.REAPPLY_AMOUNT,
    "exchanges.usePreDiscountPrice":
      pricing.discountLogic === DiscountLogic.SHOP_NOW_BONUS,
    "exchanges.blockDiscountsOnExchanges": !reapplyCodes,
    "exchanges.applyOriginalOrderDiscounts": reapplyCodes
      ? pricing.applyOriginalOrderDiscounts
      : [],
    "exchanges.applyAutomaticDiscounts": reapplyCodes
      ? pricing.applyAutomaticDiscounts
      : [],
    "exchanges.shopSiteUseCartDiscounts":
      reapplyCodes && pricing.applyShopOnSiteCartDiscounts,
    "exchanges.recreateExpiredDiscounts":
      reapplyCodes && pricing.recreateExpiredDiscounts,
    "exchanges.recreateAutomaticDiscountsAsCodes":
      reapplyCodes &&
      pricing.recreateExpiredDiscounts &&
      pricing.recreateAutomaticDiscountsAsCodes,
    "exchanges.reapplyCompareAtDiscount":
      pricing.discountLogic !== DiscountLogic.NONE &&
      pricing.reapplyCompareAtDiscount,
    "exchanges.allowCustomerDiscountCodes": pricing.allowCustomerDiscountCodes,
    "exchanges.differentPricedVariantExchanges":
      pricing.differentPricedVariantExchanges,
    "exchanges.excludeDifferentPriceVariants":
      pricing.excludeDifferentPriceVariants,
    "exchanges.variantExchangeProductTags": pricing.variantExchangeProductTags,

    "exchanges.allowInstant": customerOptions.instant,
    "exchanges.instantHoldAmount": customerOptions.instantHoldAmount,
    "exchanges.instantExchangeOnly": customerOptions.instantExchangeOnly,
    "exchanges.instantExpirationDays": customerOptions.instantExpirationDays,
    "exchanges.excludeProductTags": customerOptions.excludeProductTags,
    "exchanges.excludeProductSkus": customerOptions.excludeProductSkus,
    "exchanges.excludeProductMetafields":
      customerOptions.excludeProductMetafields,
    "exchanges.excludeVariantMetafields":
      customerOptions.excludeVariantMetafields,
    "inventory.minimum_stock": customerOptions.inventoryMinimum,
    "exchanges.hideOutOfStockVariants": customerOptions.hideOutOfStockVariants,
    "exchanges.hideUnpublishedVariants":
      customerOptions.hideUnpublishedVariants,

    "returns.returnEditing.allowCustomerCancelReturn":
      returnEditing.allowCustomerCancelReturn,
    "returns.returnEditing.allowCustomerEditExchangeAddress":
      returnEditing.allowCustomerEditExchangeAddress,

    "returns.redistributeBogoDiscounts":
      discountDistribution.redistributeBogoDiscounts,

    "returns.bundleReturnMode": bundles.returnMode,
    "returns.bundleRestockingType": bundles.restockingType,
    "returns.useShopifyNativeBundles": bundles.useShopifyNativeBundles,
    "returns.returnTogether": returnTogetherConfigs(
      bundles.returnTogetherProperties,
    ),

    deductLabelFromRefund: shippingOptions.deductLabelFromRefund,
    deductLabelFromCredit: shippingOptions.deductLabelFromCredit,
    deductLabelFromExchange: shippingOptions.deductLabelFromExchange,
  };

  const unset: string[] = [];

  // Blank means "no override", which the portal reads as an absent field, not
  // an empty string.
  const blankIsUnset: [string, string][] = [
    ["checkoutSelector", exchangeUi.checkoutSelector],
    [
      "exchanges.variantPickerColorHexMetafield",
      exchangeUi.variantPickerColorHexMetafield,
    ],
  ];
  for (const [path, value] of blankIsUnset) {
    const trimmed = value.trim();
    if (trimmed) {
      set[path] = trimmed;
    } else {
      unset.push(path);
    }
  }

  if (discountDistribution.method === null) {
    unset.push("discountDistributionMethod");
  } else {
    set.discountDistributionMethod = discountDistribution.method;
  }

  return { set, unset };
}

/**
 * Writes only what the merchant changed. Untouched fields are rebuilt from the
 * stored settings, so a stale form can't revert edits made elsewhere, and a
 * change to one field still produces every legacy field its encoding spans.
 */
export function returnsPortalTeamPatchForChanges(
  current: ReturnsPortalSettingsSource,
  changes: ReturnsPortalSettingsChanges,
): ReturnsPortalTeamPatch {
  const stored = returnsPortalSettingsFromTeam(current);
  const baseline = returnsPortalSettingsToTeamPatch(stored);
  const requested = returnsPortalSettingsToTeamPatch(
    applyChanges(stored, changes),
  );

  return {
    set: Object.fromEntries(
      Object.entries(requested.set).filter(
        ([path, value]) =>
          !(path in baseline.set) ||
          !sameSettingsValue(baseline.set[path], value),
      ),
    ),
    unset: requested.unset.filter((path) => !baseline.unset.includes(path)),
  };
}
